/**
 * Hook for managing previous reports list
 *
 * Extracted from PreviousReport.js for testability.
 * Handles fetching, sorting, and displaying user's submitted reports.
 * Supports pagination to handle large report lists (6MB API Gateway limit).
 */
import { useState, useEffect, useCallback, useRef } from 'react';

import Util from '../util/util';
import { useErrorState } from '../util/error';
import { getRouting } from '../util/storage';
import { t } from '../i18n';

// Default page size for pagination
const DEFAULT_PAGE_SIZE = 50;

/**
 * Check if a report has comments
 * @param {Object} element - Report object
 * @returns {boolean} True if report has comments
 */
export function hasComments(element) {
  if (!element) return false;
  return !(element.comments === 'No comments yet' || element.comments?.length === 0);
}

/**
 * Sort reports by timestamp descending (newest first)
 * @param {Array} reports - Array of report objects
 * @returns {Array} Sorted reports
 */
export function sortReportsByTimestamp(reports) {
  if (!Array.isArray(reports)) return [];
  return [...reports].sort((a, b) => b.timestamp - a.timestamp);
}

/**
 * Get report status icon info
 * @param {Object} report - Report object
 * @returns {{ icon: string, color: string } | null} Icon info or null
 */
export function getReportStatusIcon(report) {
  if (!report) return null;

  if (report.isClosed) {
    return { icon: 'check', color: 'green' };
  }

  if (hasComments(report)) {
    return { icon: 'inbox', color: 'rgba(255, 196, 81, 1)' };
  }

  if (report.isUrgent) {
    return { icon: 'alert-circle', color: 'red' };
  }

  return null;
}

/**
 * Main hook for managing previous reports with pagination support
 */
export default function usePreviousReports(organization, options = {}) {
  const { pageSize = DEFAULT_PAGE_SIZE, enablePagination = true } = options;

  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const nextCursorRef = useRef(null);

  const { error, hasError, handleError, clearError } = useErrorState({
    context: 'PreviousReports',
    showToasts: true,
  });

  /**
   * Build query params for API request
   * @param {string|null} cursor - Pagination cursor
   * @param {Object|null} routing - Routing data with organization mappings
   */
  const buildQueryParams = useCallback((cursor = null, routing = null) => {
    const params = { organization };

    // Add routing organizations if available
    if (routing && typeof routing === 'object') {
      const routingOrgs = [...new Set(Object.values(routing))].filter(Boolean);
      if (routingOrgs.length > 0) {
        params.organizations = routingOrgs.join(',');
      }
    }

    // Add pagination params if enabled
    if (enablePagination) {
      params.limit = pageSize;
      if (cursor) {
        params.cursor = cursor;
      }
    }

    return params;
  }, [organization, pageSize, enablePagination]);

  /**
   * Fetch user's reports from API (initial load)
   */
  const getUserReports = useCallback(async () => {
    setReportsLoading(true);
    clearError();
    nextCursorRef.current = null;

    try {
      // Fetch routing data to include routing organizations in query
      const routing = await getRouting();
      const queryParams = buildQueryParams(null, routing);
      const data = await Util.get('Resty', '/items/listUserObjects/:user', { queryParams });

      if (data.items && data.items.length > 0) {
        setReports(sortReportsByTimestamp(data.items));
      } else {
        setReports([]);
      }

      // Handle pagination response
      if (data.pagination) {
        setHasMore(data.pagination.hasMore);
        nextCursorRef.current = data.pagination.nextCursor;
      } else {
        // Non-paginated response
        setHasMore(false);
      }
    } catch (err) {
      handleError(err, { userMessage: t('reports:previous.loadFailed') });
      setReports([]);
      setHasMore(false);
    } finally {
      setReportsLoading(false);
    }
  }, [buildQueryParams, clearError, handleError]);

  /**
   * Load more reports (pagination)
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore || !nextCursorRef.current) {
      return;
    }

    setIsLoadingMore(true);

    try {
      // Fetch routing data to include routing organizations in query
      const routing = await getRouting();
      const queryParams = buildQueryParams(nextCursorRef.current, routing);
      const data = await Util.get('Resty', '/items/listUserObjects/:user', { queryParams });

      if (data.items && data.items.length > 0) {
        setReports(prev => {
          // Merge, deduplicate by uuid, and sort
          const seen = new Set(prev.map(r => r.uuid));
          const newItems = data.items.filter(r => !seen.has(r.uuid));
          return sortReportsByTimestamp([...prev, ...newItems]);
        });
      }

      // Update pagination state
      if (data.pagination) {
        setHasMore(data.pagination.hasMore);
        nextCursorRef.current = data.pagination.nextCursor;
      } else {
        setHasMore(false);
        nextCursorRef.current = null;
      }
    } catch (err) {
      handleError(err, { userMessage: t('reports:previous.loadMoreFailed'), silent: true });
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, buildQueryParams, handleError]);

  /**
   * Refresh reports (reset pagination)
   */
  const refresh = useCallback(() => {
    getUserReports();
  }, [getUserReports]);

  // Load reports on mount
  useEffect(() => {
    getUserReports();
  }, []);

  return {
    reports,
    reportsLoading,
    isLoadingMore,
    hasMore,
    error,
    hasError,
    refresh,
    loadMore,
    getUserReports,
    clearError,
  };
}

// Re-export utilities for external use
export { hasComments as checkHasComments };
